import {
  AudioLines,
  Check,
  CircleAlert,
  Clock3,
  Globe2,
  Link2,
  ListMusic,
  LoaderCircle,
  Music2,
  RefreshCw,
  Search,
  ShieldCheck,
  SkipForward,
  Trash2,
  UserRound,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, guestApi, isYouTubeInput, roomTokenFromLocation } from "./api";
import { copy, type Language } from "./i18n";
import { PublicPages } from "./PublicPages";
import { publicPageFromPath } from "./public-route";
import type { GuestSession, PublicQueueItem, RoomSnapshot, VideoResult } from "./types";

interface StoredGuest {
  token: string;
  session: GuestSession;
}

interface Toast {
  id: number;
  kind: "success" | "error";
  title: string;
  detail?: string;
  persistent?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const POLL_INTERVAL_MS = 5_000;
const LANGUAGE_STORAGE_KEY = "keeptheremote:language";

function guestStorageKey(roomToken: string): string {
  return `keeptheremote:guest:${roomToken}`;
}

function guestNameStorageKey(roomToken: string): string {
  return `keeptheremote:guest-name:${roomToken}`;
}

function readStoredGuestName(roomToken: string): string {
  try {
    return localStorage.getItem(guestNameStorageKey(roomToken))?.trim() ?? "";
  } catch {
    return "";
  }
}

function saveStoredGuestName(roomToken: string, name: string): void {
  localStorage.setItem(guestNameStorageKey(roomToken), name);
}

function readStoredGuest(roomToken: string): StoredGuest | null {
  try {
    const value = localStorage.getItem(guestStorageKey(roomToken));
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<StoredGuest>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.session?.id !== "string" ||
      typeof parsed.session.displayName !== "string"
    ) {
      return null;
    }
    return parsed as StoredGuest;
  } catch {
    return null;
  }
}

function saveStoredGuest(roomToken: string, guest: StoredGuest | null): void {
  const key = guestStorageKey(roomToken);
  if (guest) {
    localStorage.setItem(key, JSON.stringify(guest));
  } else {
    localStorage.removeItem(key);
  }
}

function initialLanguage(): Language {
  const current = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (current === "en" || current === "th") return current;
  return navigator.language.toLowerCase().startsWith("th") ? "th" : "en";
}

function formatDuration(seconds?: number): string {
  if (!seconds || !Number.isFinite(seconds)) return "";
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainingSeconds = total % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
    : `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function errorMessage(error: unknown, language: Language): string {
  const t = copy[language];
  if (!(error instanceof ApiError)) return t.genericError;
  switch (error.code) {
    case "player_offline":
    case "jukebox_offline":
      return t.offlineSubmit;
    case "room_closed":
      return t.roomClosed;
    case "room_not_found":
    case "public_link_unavailable":
      return t.unavailable;
    case "requests_disabled":
      return t.requestsPaused;
    case "request_cooldown":
    case "rate_limited":
      return error.retryAfterSeconds ? `${t.cooldown} (${error.retryAfterSeconds}s)` : t.cooldown;
    case "guest_request_limit":
      return t.requestLimit;
    case "guest_skip_unavailable":
      return t.skipUnavailable;
    case "duplicate_video":
      return t.duplicate;
    case "queue_full":
      return t.queueFull;
    case "queue_item_not_found":
    case "queue_item_not_actionable":
      return t.removeUnavailable;
    case "video_too_long":
      return t.tooLong;
    case "live_video_rejected":
    case "live_stream_rejected":
    case "premiere_rejected":
      return t.liveRejected;
    case "short_rejected":
      return t.shortRejected;
    case "music_only":
      return t.musicOnlyRejected;
    case "music_category_unavailable":
      return t.musicCategoryUnavailable;
    case "guest_unauthorized":
    case "session_invalid":
      return t.sessionExpired;
    case "invalid_youtube_url":
    case "invalid_video_id":
      return t.invalidVideo;
    case "video_unavailable":
      return t.videoUnavailable;
    case "invalid_video_duration":
      return t.durationUnavailable;
    case "youtube_quota_exceeded":
    case "youtube_not_configured":
    case "youtube_unavailable":
    case "youtube_api_error":
    case "youtube_auth_failed":
    case "youtube_auth_expired":
    case "youtube_auth_refresh_failed":
      return t.quota;
    default:
      return language === "en" ? error.message || t.genericError : t.genericError;
  }
}

function errorToastBehavior(error: unknown): { persistent: boolean; retryable: boolean } {
  if (!(error instanceof ApiError)) {
    return { persistent: true, retryable: true };
  }
  const retryable = error.status >= 500 || error.code.startsWith("youtube_");
  const sessionExpired = ["guest_unauthorized", "session_invalid"].includes(error.code);
  return {
    persistent: retryable || sessionExpired,
    retryable,
  };
}

function NotificationToast({
  toast,
  dismissLabel,
  onDismiss,
}: {
  toast: Toast | null;
  dismissLabel: string;
  onDismiss: () => void;
}) {
  const action = toast?.action;
  return (
    <div
      className={`toast toast--${toast?.kind ?? "success"} ${toast ? "toast--visible" : ""}`}
      role={toast?.kind === "error" ? "alert" : "status"}
      aria-live={toast?.kind === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      aria-hidden={!toast}
    >
      <span className="toast__icon">
        {toast?.kind === "error" ? <CircleAlert size={18} /> : <Check size={18} />}
      </span>
      <div className="toast__body">
        <strong>{toast?.title}</strong>
        {toast?.detail ? (
          <span className="toast__detail">
            <Clock3 size={13} />
            {toast.detail}
          </span>
        ) : null}
        {action ? (
          <div className="toast__actions">
            <button
              className="toast__action"
              type="button"
              onClick={() => {
                onDismiss();
                action.onClick();
              }}
            >
              {action.label}
            </button>
          </div>
        ) : null}
      </div>
      {toast?.kind === "error" ? (
        <button
          className="toast__dismiss"
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  );
}

function Thumbnail({
  src,
  title,
  className = "",
}: {
  src: string | undefined;
  title: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`thumbnail thumbnail--fallback ${className}`} aria-hidden="true">
        <Music2 size={24} strokeWidth={1.8} />
      </div>
    );
  }
  return (
    <img
      className={`thumbnail ${className}`}
      src={src}
      alt={title}
      loading="lazy"
      onError={() => setFailed(true)}
      referrerPolicy="no-referrer"
    />
  );
}

function NextVideoPreview({
  video,
  language,
  guestName,
  className = "",
}: {
  video: PublicQueueItem;
  language: Language;
  guestName: string | undefined;
  className?: string;
}) {
  const t = copy[language];
  return (
    <div className={`next-video ${className}`}>
      <Thumbnail
        key={video.videoId}
        src={video.thumbnailUrl || `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`}
        title={video.title}
        className="next-video__thumbnail"
      />
      <div className="next-video__copy">
        <strong>{video.title}</strong>
        {video.channelTitle ? <span>{video.channelTitle}</span> : null}
        <small>
          {t.requestedBy} {video.requestedBy === guestName ? t.you : video.requestedBy}
        </small>
      </div>
    </div>
  );
}

function StatusPill({ snapshot, language }: { snapshot: RoomSnapshot; language: Language }) {
  const t = copy[language];
  const state = !snapshot.room.isOpen
    ? "closed"
    : !snapshot.room.playerOnline
      ? "offline"
      : !snapshot.room.requestsEnabled
        ? "paused"
        : "online";
  const label =
    state === "online"
      ? t.online
      : state === "offline"
        ? t.offline
        : state === "paused"
          ? t.requestsPaused
          : t.roomClosed;

  return (
    <div className={`status-pill status-pill--${state}`}>
      {state === "online" ? <Wifi size={14} /> : <WifiOff size={14} />}
      <span>{label}</span>
    </div>
  );
}

function NowPlaying({
  snapshot,
  language,
  guestName,
  canSkip,
  skipping,
  onSkip,
}: {
  snapshot: RoomSnapshot;
  language: Language;
  guestName: string | undefined;
  canSkip: boolean;
  skipping: boolean;
  onSkip: () => void;
}) {
  const t = copy[language];
  const current = snapshot.currentVideo;
  const nextVideo = snapshot.queue[0];
  const progress =
    current?.durationSeconds && current.positionSeconds !== undefined
      ? Math.min(100, Math.max(0, (current.positionSeconds / current.durationSeconds) * 100))
      : 0;

  return (
    <section className="now-card" aria-labelledby="now-playing-title">
      <div className="now-card__glow" aria-hidden="true" />
      <div className="section-label">
        <AudioLines size={15} />
        <span id="now-playing-title">{t.nowPlaying}</span>
      </div>
      {current ? (
        <>
          <div className="now-card__content">
            <Thumbnail
              key={current.videoId}
              src={
                current.thumbnailUrl || `https://i.ytimg.com/vi/${current.videoId}/hqdefault.jpg`
              }
              title={current.title}
              className="now-card__thumbnail"
            />
            <div className="now-card__copy">
              <h2>{current.title}</h2>
              {current.channelTitle ? <p>{current.channelTitle}</p> : null}
              {current.requestedBy ? (
                <p className="now-card__requestor">
                  {t.requestedBy} {current.requestedBy === guestName ? t.you : current.requestedBy}
                </p>
              ) : null}
              {current.durationSeconds ? (
                <div className="now-card__timeline" aria-label={`${Math.round(progress)}%`}>
                  <span style={{ width: `${progress}%` }} />
                </div>
              ) : null}
            </div>
            <div className="equalizer" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </div>
          </div>
          {canSkip && nextVideo ? (
            <div className="skip-next">
              <div className="skip-next__heading">
                <span>{t.nextRequest}</span>
                <strong>{t.playNextTitle}</strong>
              </div>
              <div className="skip-next__content">
                <NextVideoPreview video={nextVideo} language={language} guestName={guestName} />
                <button type="button" onClick={onSkip} disabled={skipping}>
                  {skipping ? (
                    <LoaderCircle className="spin" size={17} />
                  ) : (
                    <SkipForward size={17} />
                  )}
                  {skipping ? t.skipping : t.playNow}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="now-card__empty">
          <div className="soft-icon">
            <Music2 size={24} />
          </div>
          <div>
            <h2>{t.waitingForPlayer}</h2>
            <p>{snapshot.room.playerOnline ? t.queueEmptyHint : t.offlineSubmit}</p>
          </div>
        </div>
      )}
    </section>
  );
}

function SkipPrompt({
  open,
  language,
  nextVideo,
  guestName,
  busy,
  onSkip,
  onClose,
}: {
  open: boolean;
  language: Language;
  nextVideo: PublicQueueItem | undefined;
  guestName: string | undefined;
  busy: boolean;
  onSkip: () => void;
  onClose: () => void;
}) {
  const t = copy[language];
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose, open]);
  if (!open || !nextVideo) return null;
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="skip-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="skip-dialog-title"
        aria-describedby="skip-dialog-detail"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          className="skip-dialog__close"
          type="button"
          onClick={onClose}
          aria-label={t.dismiss}
        >
          <X size={18} />
        </button>
        <span className="skip-dialog__eyebrow">{t.nextRequest}</span>
        <h2 id="skip-dialog-title">{t.skipPromptTitle}</h2>
        <p id="skip-dialog-detail">{t.skipPromptHint}</p>
        <NextVideoPreview
          video={nextVideo}
          language={language}
          guestName={guestName}
          className="skip-dialog__preview"
        />
        <div className="skip-dialog__actions">
          <button
            className="button button--quiet"
            type="button"
            onClick={onClose}
            disabled={busy}
            autoFocus
          >
            {t.keepPlaying}
          </button>
          <button className="button button--primary" type="button" onClick={onSkip} disabled={busy}>
            {busy ? <LoaderCircle className="spin" size={17} /> : <SkipForward size={17} />}
            {busy ? t.skipping : t.playNow}
          </button>
        </div>
      </section>
    </div>
  );
}

function JoinCard({
  language,
  busy,
  initialName,
  onJoin,
}: {
  language: Language;
  busy: boolean;
  initialName: string;
  onJoin: (name: string) => Promise<void>;
}) {
  const t = copy[language];
  const [name, setName] = useState(initialName);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = name.trim();
    if (normalized.length >= 1) {
      void onJoin(normalized);
    }
  };

  return (
    <section className="join-card" aria-labelledby="join-title">
      <div className="join-card__icon">
        <UserRound size={22} />
      </div>
      <div className="join-card__intro">
        <h2 id="join-title">{t.joinTitle}</h2>
        <p>{t.joinHint}</p>
      </div>
      <form onSubmit={submit}>
        <label className="sr-only" htmlFor="guest-name">
          {t.namePlaceholder}
        </label>
        <input
          id="guest-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={32}
          autoComplete="nickname"
          placeholder={t.namePlaceholder}
          autoFocus
        />
        <button className="button button--primary" disabled={busy || !name.trim()} type="submit">
          {busy ? <LoaderCircle className="spin" size={17} /> : <UserRound size={17} />}
          {t.join}
        </button>
      </form>
    </section>
  );
}

function SearchPanel({
  language,
  disabled,
  musicOnly,
  results,
  searching,
  addingVideoId,
  onSearch,
  onAdd,
}: {
  language: Language;
  disabled: boolean;
  musicOnly: boolean;
  results: VideoResult[];
  searching: boolean;
  addingVideoId: string | null;
  onSearch: (query: string) => Promise<boolean>;
  onAdd: (video: VideoResult) => Promise<void>;
}) {
  const t = copy[language];
  const [query, setQuery] = useState("");
  const isDirectVideo = isYouTubeInput(query);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const submittedQuery = query.trim();
    if (!submittedQuery) return;
    void onSearch(submittedQuery).then((succeeded) => {
      if (succeeded && isYouTubeInput(submittedQuery)) setQuery("");
    });
  };

  return (
    <section className="search-panel" aria-labelledby="search-title">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">01</span>
          <h2 id="search-title">{t.searchTitle}</h2>
          <p>{t.searchHint}</p>
          {musicOnly ? <p>{t.musicOnlyHint}</p> : null}
        </div>
        <div className="soft-icon">
          <ListMusic size={21} />
        </div>
      </div>
      <form className="search-form" onSubmit={submit}>
        <div className="search-input">
          {isDirectVideo ? <Link2 size={19} /> : <Search size={19} />}
          <label className="sr-only" htmlFor="video-search">
            {t.searchPlaceholder}
          </label>
          <input
            id="video-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.searchPlaceholder}
            disabled={disabled}
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        <button
          className="button button--search"
          type="submit"
          disabled={disabled || searching || !query.trim()}
        >
          {searching ? (
            <LoaderCircle className="spin" size={18} />
          ) : isDirectVideo ? (
            <Link2 size={18} />
          ) : (
            <Search size={18} />
          )}
          {searching
            ? isDirectVideo
              ? t.checkingAndAdding
              : t.searching
            : isDirectVideo
              ? t.addVideo
              : t.search}
        </button>
      </form>

      {results.length > 0 ? (
        <div className="results" aria-labelledby="results-title">
          <div className="results__heading">
            <h3 id="results-title">{t.resultTitle}</h3>
            <span>{results.length}</span>
          </div>
          <div className="results__list">
            {results.map((video) => (
              <article className="result-card" key={video.videoId}>
                <div className="result-card__media">
                  <Thumbnail src={video.thumbnailUrl} title={video.title} />
                  {video.durationSeconds > 0 ? (
                    <span className="duration">{formatDuration(video.durationSeconds)}</span>
                  ) : null}
                </div>
                <div className="result-card__copy">
                  <h4>{video.title}</h4>
                  <p>{video.channelTitle}</p>
                </div>
                <button
                  className="button button--add"
                  type="button"
                  disabled={disabled || addingVideoId !== null}
                  onClick={() => void onAdd(video)}
                  aria-label={`${t.add}: ${video.title}`}
                >
                  {addingVideoId === video.videoId ? (
                    <LoaderCircle className="spin" size={17} />
                  ) : (
                    <span className="add-mark" aria-hidden="true">
                      +
                    </span>
                  )}
                  <span>{addingVideoId === video.videoId ? t.adding : t.add}</span>
                </button>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function QueuePanel({
  queue,
  guest,
  language,
  removingItemId,
  onRemove,
}: {
  queue: PublicQueueItem[];
  guest: StoredGuest | null;
  language: Language;
  removingItemId: string | null;
  onRemove: (item: PublicQueueItem) => Promise<void>;
}) {
  const t = copy[language];

  return (
    <section className="queue-panel" aria-labelledby="queue-title">
      <div className="panel-heading panel-heading--queue">
        <div>
          <span className="eyebrow">02</span>
          <h2 id="queue-title">{t.queueTitle}</h2>
        </div>
        <span className="queue-count">
          {queue.length} {queue.length === 1 ? t.songSingular : t.songPlural}
        </span>
      </div>

      {queue.length === 0 ? (
        <div className="empty-queue">
          <div className="queue-orbit" aria-hidden="true">
            <Music2 size={21} />
          </div>
          <h3>{t.queueEmpty}</h3>
          <p>{t.queueEmptyHint}</p>
        </div>
      ) : (
        <ol className="queue-list">
          {queue.map((item, index) => {
            const isGuest = Boolean(guest && item.canRemove);
            return (
              <li key={item.id}>
                <span className="queue-number">{String(index + 1).padStart(2, "0")}</span>
                <Thumbnail src={item.thumbnailUrl} title={item.title} />
                <div className="queue-item__copy">
                  <h3>{item.title}</h3>
                  {item.channelTitle ? <p>{item.channelTitle}</p> : null}
                  <p className="queue-item__requestor">
                    {t.requestedBy}{" "}
                    <strong className={isGuest ? "is-you" : ""}>
                      {isGuest ? t.you : item.requestedBy}
                    </strong>
                  </p>
                </div>
                <div className="queue-item__actions">
                  {item.durationSeconds ? (
                    <span className="queue-duration">{formatDuration(item.durationSeconds)}</span>
                  ) : null}
                  {item.canRemove ? (
                    <button
                      className="queue-remove"
                      type="button"
                      disabled={removingItemId !== null}
                      onClick={() => void onRemove(item)}
                      aria-label={`${t.removeRequest}: ${item.title}`}
                      title={t.removeRequest}
                    >
                      {removingItemId === item.id ? (
                        <LoaderCircle className="spin" size={16} />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function RoomError({
  message,
  language,
  retry,
}: {
  message: string;
  language: Language;
  retry?: () => void;
}) {
  const t = copy[language];
  return (
    <main className="error-page">
      <div className="brand-mark">
        <AudioLines size={24} />
      </div>
      <p className="brand-name">Keep the Remote</p>
      <h1>{t.unavailable}</h1>
      <p>{message}</p>
      {retry ? (
        <button className="button button--primary" type="button" onClick={retry}>
          <RefreshCw size={17} />
          {t.retry}
        </button>
      ) : null}
    </main>
  );
}

function RoomApp() {
  const roomToken = useMemo(() => roomTokenFromLocation(), []);
  const [language, setLanguage] = useState<Language>(initialLanguage);
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [roomError, setRoomError] = useState<string | null>(null);
  const [guest, setGuest] = useState<StoredGuest | null>(() =>
    roomToken ? readStoredGuest(roomToken) : null,
  );
  const [joining, setJoining] = useState(false);
  const [results, setResults] = useState<VideoResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingVideoId, setAddingVideoId] = useState<string | null>(null);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [skipping, setSkipping] = useState(false);
  const [skipPromptOpen, setSkipPromptOpen] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const searchAbort = useRef<AbortController | null>(null);
  const promptedAutoplayVideos = useRef(new Set<string>());
  const toastId = useRef(0);
  const t = copy[language];

  const refreshRoom = useCallback(
    async (silent = false) => {
      if (!roomToken) return;
      if (!silent) setLoading(true);
      try {
        const next = await guestApi.getRoom(roomToken, guest?.token);
        setSnapshot(next);
        setRoomError(null);
      } catch (error) {
        if (!silent) {
          setRoomError(errorMessage(error, language));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [guest?.token, language, roomToken],
  );

  useEffect(() => {
    if (!roomToken) {
      setLoading(false);
      return;
    }
    void refreshRoom();
    const interval = window.setInterval(() => void refreshRoom(true), POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [refreshRoom, roomToken]);

  useEffect(() => {
    if (!toast || toast.persistent) return;
    const timeout = window.setTimeout(() => setToast(null), toast.kind === "error" ? 8_000 : 5_000);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    document.title = snapshot?.room.name
      ? `Keep the Remote — ${snapshot.room.name}`
      : "Keep the Remote";
  }, [snapshot?.room.name]);

  useEffect(() => {
    const currentVideoId = snapshot?.currentVideo?.videoId;
    if (!guest || !snapshot?.guestCanSkipCurrent || !currentVideoId) {
      setSkipPromptOpen(false);
      return;
    }
    if (!promptedAutoplayVideos.current.has(currentVideoId)) {
      promptedAutoplayVideos.current.add(currentVideoId);
      setSkipPromptOpen(true);
    }
  }, [guest, snapshot?.currentVideo?.videoId, snapshot?.guestCanSkipCurrent]);

  const changeLanguage = () => {
    const next = language === "en" ? "th" : "en";
    setLanguage(next);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    document.documentElement.lang = next;
  };

  const showToast = (next: Omit<Toast, "id">) => {
    toastId.current += 1;
    setToast({ id: toastId.current, ...next });
  };

  const showErrorToast = (error: unknown, retry?: () => void) => {
    const behavior = errorToastBehavior(error);
    showToast({
      kind: "error",
      title: errorMessage(error, language),
      persistent: behavior.persistent,
      ...(behavior.retryable && retry
        ? {
            action: {
              label: t.retry,
              onClick: retry,
            },
          }
        : {}),
    });
  };

  const join = async (displayName: string) => {
    if (!roomToken) return;
    setJoining(true);
    setToast(null);
    try {
      const response = await guestApi.createSession(roomToken, displayName);
      const next = { token: response.guestToken, session: response.session };
      saveStoredGuestName(roomToken, response.session.displayName);
      saveStoredGuest(roomToken, next);
      setGuest(next);
    } catch (error) {
      showErrorToast(error, () => void join(displayName));
    } finally {
      setJoining(false);
    }
  };

  const leave = () => {
    if (!roomToken) return;
    saveStoredGuest(roomToken, null);
    setGuest(null);
    setResults([]);
    setToast(null);
  };

  const enqueueVideo = async (video: VideoResult) => {
    if (!roomToken || !guest) return;
    const response = await guestApi.addToQueue(roomToken, video.videoId, guest.token);
    showToast({
      kind: "success",
      title: t.added,
      detail: `${t.position}: ${response.position}`,
    });
    setResults((current) => current.filter((item) => item.videoId !== video.videoId));
    await refreshRoom(true);
  };

  const search = async (query: string): Promise<boolean> => {
    if (!roomToken || !guest) return false;
    searchAbort.current?.abort();
    const controller = new AbortController();
    searchAbort.current = controller;
    setSearching(true);
    setToast(null);
    try {
      if (isYouTubeInput(query)) {
        setResults([]);
        const response = await guestApi.resolve(roomToken, query, guest.token, controller.signal);
        await enqueueVideo(response.video);
      } else {
        const response = await guestApi.search(roomToken, query, guest.token, controller.signal);
        setResults(response.results);
        if (response.results.length === 0) {
          showToast({
            kind: "error",
            title: t.noResults,
          });
        }
      }
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return false;
      if (error instanceof ApiError && error.status === 401) {
        leave();
      }
      setResults([]);
      showErrorToast(error, () => void search(query));
      return false;
    } finally {
      if (searchAbort.current === controller) setSearching(false);
    }
  };

  const add = async (video: VideoResult) => {
    if (!roomToken || !guest) return;
    setAddingVideoId(video.videoId);
    setToast(null);
    try {
      await enqueueVideo(video);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        leave();
      }
      showErrorToast(error, () => void add(video));
    } finally {
      setAddingVideoId(null);
    }
  };

  const removeFromQueue = async (item: PublicQueueItem) => {
    if (!roomToken || !guest || !item.canRemove) return;
    setRemovingItemId(item.id);
    setToast(null);
    try {
      await guestApi.removeFromQueue(roomToken, item.id, guest.token);
      setSnapshot((current) =>
        current
          ? { ...current, queue: current.queue.filter((queueItem) => queueItem.id !== item.id) }
          : current,
      );
      showToast({
        kind: "success",
        title: t.removed,
      });
      await refreshRoom(true);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        leave();
      }
      showErrorToast(error, () => void removeFromQueue(item));
      await refreshRoom(true);
    } finally {
      setRemovingItemId(null);
    }
  };

  const skipCurrent = async () => {
    if (!roomToken || !guest || !snapshot?.guestCanSkipCurrent) return;
    setSkipping(true);
    setToast(null);
    try {
      await guestApi.skipCurrent(roomToken, guest.token);
      setSkipPromptOpen(false);
      showToast({
        kind: "success",
        title: t.skipRequested,
      });
      await refreshRoom(true);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        leave();
      }
      setSkipPromptOpen(false);
      showErrorToast(error, () => void skipCurrent());
      await refreshRoom(true);
    } finally {
      setSkipping(false);
    }
  };

  if (!roomToken) {
    return <RoomError message={t.invalidRoom} language={language} />;
  }

  if (loading && !snapshot) {
    return (
      <div className="loading-page" aria-label={t.loading}>
        <div className="loading-mark">
          <AudioLines size={25} />
        </div>
        <div className="loading-line" />
        <div className="loading-line loading-line--short" />
      </div>
    );
  }

  if (!snapshot) {
    return (
      <RoomError
        message={roomError ?? t.genericError}
        language={language}
        retry={() => void refreshRoom()}
      />
    );
  }

  const acceptingRequests =
    snapshot.room.isOpen &&
    snapshot.room.requestsEnabled &&
    (snapshot.room.playerOnline || snapshot.room.acceptOfflineRequests === true);
  const disabledReason = !snapshot.room.isOpen
    ? t.roomClosed
    : !snapshot.room.requestsEnabled
      ? t.requestsPaused
      : !snapshot.room.playerOnline && !snapshot.room.acceptOfflineRequests
        ? t.offlineSubmit
        : null;

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href={window.location.pathname} aria-label="Keep the Remote">
          <span className="brand-mark">
            <AudioLines size={21} />
          </span>
          <span>
            <strong>Keep the Remote</strong>
            <small>Queue</small>
          </span>
        </a>
        <div className="header-actions">
          <StatusPill snapshot={snapshot} language={language} />
          <button
            className="language-button"
            type="button"
            onClick={changeLanguage}
            aria-label={language === "en" ? "เปลี่ยนเป็นภาษาไทย" : "Switch to English"}
          >
            <Globe2 size={16} />
            {language === "en" ? "TH" : "EN"}
          </button>
        </div>
      </header>

      <main>
        <NowPlaying
          snapshot={snapshot}
          language={language}
          guestName={guest?.session.displayName}
          canSkip={Boolean(guest && snapshot.guestCanSkipCurrent)}
          skipping={skipping}
          onSkip={() => setSkipPromptOpen(true)}
        />

        {guest ? (
          <QueuePanel
            queue={snapshot.queue}
            guest={guest}
            language={language}
            removingItemId={removingItemId}
            onRemove={removeFromQueue}
          />
        ) : null}

        {disabledReason ? (
          <div className="availability-banner" role="status">
            <WifiOff size={18} />
            <div>
              <strong>{disabledReason}</strong>
              <span>{snapshot.room.playerOnline ? t.queueEmptyHint : t.offlineSubmit}</span>
            </div>
            <button
              type="button"
              onClick={() => void refreshRoom()}
              aria-label={t.refresh}
              title={t.refresh}
            >
              <RefreshCw size={17} />
            </button>
          </div>
        ) : null}

        {!guest ? (
          <JoinCard
            language={language}
            busy={joining}
            initialName={readStoredGuestName(roomToken)}
            onJoin={join}
          />
        ) : (
          <div className="guest-strip">
            <div>
              <span className="guest-avatar">
                {guest.session.displayName.slice(0, 1).toUpperCase()}
              </span>
              <p>
                <small>{t.you}</small>
                <strong>{guest.session.displayName}</strong>
              </p>
            </div>
            <button type="button" onClick={leave}>
              {t.leave}
            </button>
          </div>
        )}

        {guest ? (
          <div className="content-grid">
            <SearchPanel
              language={language}
              disabled={!acceptingRequests}
              musicOnly={snapshot.room.musicOnly === true}
              results={results}
              searching={searching}
              addingVideoId={addingVideoId}
              onSearch={search}
              onAdd={add}
            />
          </div>
        ) : null}
      </main>

      <footer>
        <div className="footer-lockup">
          <ShieldCheck size={16} />
          <span>{t.serviceNote}</span>
        </div>
        <span className="footer-dot" aria-hidden="true" />
        <span>Keep the Remote</span>
      </footer>

      <NotificationToast toast={toast} dismissLabel={t.dismiss} onDismiss={() => setToast(null)} />
      <SkipPrompt
        open={skipPromptOpen && Boolean(guest && snapshot.guestCanSkipCurrent)}
        language={language}
        nextVideo={snapshot.queue[0]}
        guestName={guest?.session.displayName}
        busy={skipping}
        onSkip={() => void skipCurrent()}
        onClose={() => setSkipPromptOpen(false)}
      />
    </div>
  );
}

export function App() {
  const publicPage = publicPageFromPath(window.location.pathname);
  return publicPage ? <PublicPages page={publicPage} /> : <RoomApp />;
}
